/**
 * Comments on feed updates – list + add comment.
 */

import React, { useState, useEffect } from 'react';
import { useAuth } from '../contexts/AuthContext';
import {
  collection,
  query,
  where,
  orderBy,
  getDocs,
  addDoc,
  deleteDoc,
  doc,
  serverTimestamp,
} from 'firebase/firestore';
import { db } from '../firebase/config';
import { getUserDisplayName } from '../utils/nameFormat';
import { formatDate } from '../utils/dateFormat';
import './FeedComments.css';

export default function FeedComments({ feedItemId }) {
  const { currentUser, userProfile } = useAuth();
  const [comments, setComments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [expanded, setExpanded] = useState(false);
  const [newComment, setNewComment] = useState('');
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    if (!feedItemId) {
      setLoading(false);
      setComments([]);
      return;
    }
    const q = query(
      collection(db, 'feedComments'),
      where('feedItemId', '==', feedItemId),
      orderBy('createdAt', 'asc')
    );
    const unsub = () => {};
    getDocs(q)
      .then((snap) => {
        setComments(snap.docs.map((d) => ({ id: d.id, ...d.data() })));
      })
      .catch(() => setComments([]))
      .finally(() => setLoading(false));
    return unsub;
  }, [feedItemId]);

  async function handleSubmit(e) {
    e.preventDefault();
    const text = (newComment || '').trim();
    if (!text || !currentUser?.uid || !feedItemId || submitting) return;
    setSubmitting(true);
    try {
      const displayName = getUserDisplayName({
        displayName: userProfile?.displayName || currentUser?.displayName,
        email: currentUser?.email,
      });
      const ref = await addDoc(collection(db, 'feedComments'), {
        feedItemId,
        userId: currentUser.uid,
        userDisplayName: displayName || 'Someone',
        text,
        createdAt: serverTimestamp(),
      });
      setNewComment('');
      setComments((prev) => [
        ...prev,
        {
          id: ref.id,
          userId: currentUser.uid,
          userDisplayName: displayName || 'Someone',
          text,
          createdAt: { toDate: () => new Date() },
        },
      ]);
      setExpanded(true);
    } catch (err) {
      console.warn('[FeedComments] add failed', err);
    } finally {
      setSubmitting(false);
    }
  }

  async function handleDelete(commentId, commentUserId) {
    if (!currentUser?.uid || commentUserId !== currentUser.uid) return;
    try {
      await deleteDoc(doc(db, 'feedComments', commentId));
      setComments((prev) => prev.filter((c) => c.id !== commentId));
    } catch (err) {
      console.warn('[FeedComments] delete failed', err);
    }
  }

  function formatCommentTime(t) {
    if (!t) return '';
    const d = t.toDate ? t.toDate() : new Date(t);
    const now = new Date();
    const diffMs = now - d;
    const diffM = Math.floor(diffMs / 60000);
    const diffH = Math.floor(diffMs / 3600000);
    const diffD = Math.floor(diffMs / 86400000);
    if (diffM < 1) return 'Just now';
    if (diffM < 60) return `${diffM}m ago`;
    if (diffH < 24) return `${diffH}h ago`;
    if (diffD < 7) return `${diffD}d ago`;
    return formatDate(d);
  }

  if (!currentUser) return null;

  const hasComments = comments.length > 0;

  return (
    <div className="feed-comments">
      {hasComments && (
        <button
          type="button"
          className="feed-comments-toggle"
          onClick={() => setExpanded(!expanded)}
          aria-expanded={expanded}
        >
          {expanded ? 'Hide' : 'View'} {comments.length} comment{comments.length !== 1 ? 's' : ''}
        </button>
      )}
      <div className="feed-comments-panel">
        {loading && <span className="feed-comments-loading">…</span>}
        {!loading && hasComments && expanded && (
          <ul className="feed-comments-list">
            {comments.map((c) => (
              <li key={c.id} className="feed-comment-item">
                <span className="feed-comment-line">
                  <span className="feed-comment-author">{c.userDisplayName}</span>
                  <span className="feed-comment-text">{c.text}</span>
                </span>
                <span className="feed-comment-meta">
                  {formatCommentTime(c.createdAt)}
                  {c.userId === currentUser.uid && (
                    <button
                      type="button"
                      className="feed-comment-delete"
                      onClick={() => handleDelete(c.id, c.userId)}
                      aria-label="Delete comment"
                    >
                      Delete
                    </button>
                  )}
                </span>
              </li>
            ))}
          </ul>
        )}
        <form onSubmit={handleSubmit} className="feed-comments-form">
          <input
            type="text"
            value={newComment}
            onChange={(e) => setNewComment(e.target.value)}
            placeholder="Add a comment…"
            className="feed-comments-input"
            maxLength={500}
            disabled={submitting}
            aria-label="Comment text"
          />
          <button
            type="submit"
            className="feed-comments-post"
            disabled={!newComment.trim() || submitting}
            aria-label="Post comment"
          >
            Post
          </button>
        </form>
      </div>
    </div>
  );
}
